import { AppBarData } from "@/appBar/appBarDomain";
import { isFailure, isOk, isPending, SubscriptionTypeModel } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import { FeatureToggles } from "@/common/featureToggles";
import { PaidSubscription, SubscriptionType } from "@/common/subscription";
import { AiPrompt } from "@/components-v2/AiPrompt";
import { Alert } from "@/components-v2/Alert";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { Box, Card, Container, Grid, GridItem, VBox } from "@/components-v2/Box";
import { boxClassNames } from "@/components-v2/boxHelpers";
import { Button, CloseButton, LinkButton } from "@/components-v2/Button";
import { Checkbox, CheckboxProps } from "@/components-v2/Checkbox";
import { DataTable, RowAction, TableColumn } from "@/components-v2/DataTable";
import { Dialog } from "@/components-v2/Dialog";
import { DocumentPreview } from "@/components-v2/DocumentPreview";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { EmptyState } from "@/components-v2/EmptyState";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Icon } from "@/components-v2/Icon";
import { InfoButton } from "@/components-v2/InfoButton";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Popover } from "@/components-v2/Popover";
import { Select } from "@/components-v2/Select.tsx";
import { StarButton } from "@/components-v2/StarButton";
import { Tabs } from "@/components-v2/Tabs";
import { Tag } from "@/components-v2/Tag";
import { TextInput } from "@/components-v2/TextInput";
import {
  decisionCategoryLabels,
  getDecisionActionLabel,
  isDecisionCategoryNew,
  isDecisionTypeNew,
  ProjectCompany,
  ProjectListItem,
} from "@/dbot/common/commonDomain";
import { ProjectStatusTag } from "@/dbot/project/commonProjectComponents";
import { ConfirmDeleteProjectDialog } from "@/dbot/project/projectComponents";
import { ChangePermissionDialogButton } from "@/components-v2/ChangePermissionDialogButton";
import { requiresBasicSubscription, requiresProSubscription } from "@/payment/Payment";
import { OrderSubscriptionUpgrade } from "@/payment/paymentComponents";
import { agenticUploadConfig } from "@/shareholderRegister/transactions/agentic/agenticTransactionChatDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { useDispatch, useI18n, useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { Markdown } from "@/text/Markdown";
import { classNames } from "@/utils/classNames";
import { preventDefault } from "@/utils/events";
import { when } from "@/utils/functions.ts";
import { useElementId } from "@/utils/reactHelpers";
import { isMobileView } from "@/utils/screen";
import { compact } from "lodash";
import { observer } from "mobx-react";
import { FC, useMemo, useRef } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import IncorporationIllustration from "./incorporation.svg";
import {
  addProjectEvents,
  addProjectOptions,
  canAddProject,
  cancelCreateBatchProject,
  clearProjectListSelection,
  closeExistingDecisionPreview,
  containsProjects,
  createBatchProject,
  createProjectName,
  DecisionsByCategory,
  deleteSubmit,
  deselectAllCompanies,
  ExistingDecisionPreview,
  getExistingDecisionPreviewContent,
  getExistingDecisionPreviewTabs,
  getExistingDecisions,
  getExistingDecisionsCompanySelect,
  getExistionDecisionAttachments,
  getFilteredExistingDecisions,
  getMatterTypeSelect,
  getProjectListDecisionsByCategory,
  getProjectListDecisionsByCategoryForBatch,
  getProjects,
  getPromptSources,
  getSelectedDecisionLabel,
  getTabs,
  hasOtherPortfolioProjects,
  hasOwnProjects,
  isCompanySelected,
  isMainCompany,
  isProjectListEmpty,
  isShowingAll,
  openAttachment,
  openChangePermissionDialog,
  openDuplicateProjectDialog,
  openProject,
  ProjectListState,
  projectPrimaryCompanyName,
  removePromptSource,
  selectAllCompanies,
  selectForDelete,
  setExistingDecisionsFilter,
  setPrompt,
  setPromptInputCtrlRef,
  showAll,
  toggleBatchDecision,
  toggleCompanySelected,
  toggleDecision,
  toggleDecisionStarred,
  toggleExistingDecision,
  toggleExistingDecisionPreview,
  toggleShowOtherProjectsSelected,
} from "./projectListDomain";
import emptyStateStarredMatters from "@/assets/img/empty-state-starred-matters.svg";
import emptyStateRecentMatters from "@/assets/img/empty-state-no-recent-matters.svg";

export const DecisionLabel: FC<{
  decisionType: DecisionType;
  isDisabled?: boolean;
  isRenewalCandidate?: boolean;
}> = ({ decisionType, isDisabled, isRenewalCandidate }) => {
  const { t } = useTranslation();
  const badge = isDisabled
    ? t("dbot.coming-soon")
    : isDecisionTypeNew(decisionType)
      ? t("dbot.new")
      : isRenewalCandidate
        ? t("dbot.poa.candidate-renewal")
        : null;
  const badgeColor = isRenewalCandidate ? "warning" : "info";
  return (
    <div>
      {t(getDecisionActionLabel(decisionType))}{" "}
      {badge && (
        <Tag
          label={badge}
          color={badgeColor}
          compact
          tooltip={isRenewalCandidate ? t("dbot.poa.candidate-renewal-info") : undefined}
        />
      )}
    </div>
  );
};

export const WhatWouldYouLikeToDo: FC<{
  decisionsByCategory: DecisionsByCategory[];
  toggleDecision: (decision: DecisionType) => void;
}> = observer(({ decisionsByCategory, toggleDecision }) => (
  <DecisionList decisionsByCategory={decisionsByCategory} toggleDecision={toggleDecision} />
));

const DecisionList: FC<{
  decisionsByCategory: DecisionsByCategory[];
  toggleDecision: (decision: DecisionType) => void;
}> = observer(({ decisionsByCategory, toggleDecision }) => {
  const { t } = useTranslation();
  return (
    <div className={classNames("columns-1", "md:columns-2", "xl:columns-3", "2xl:columns-4")}>
      {decisionsByCategory.map(
        ({ category, decisions, requiredSubscriptionType, currentSubscription }) => (
          <VBox key={category} marginBottom={"lg"} className={"break-inside-avoid"}>
            <DecisionTypeHeader
              label={t(decisionCategoryLabels[category])}
              isNew={isDecisionCategoryNew(category)}
              subscriptionTypeModel={{
                currentSubscription,
                requiredSubscriptionType,
              }}
            />
            {decisions.map(
              ({
                decisionType,
                isSelected,
                isDisabled,
                isExisting,
                isRenewalCandidate,
                isBlockedByExclusiveDecision,
              }) => (
                <div key={decisionType}>
                  <Checkbox
                    label={
                      <DecisionLabel
                        decisionType={decisionType}
                        isDisabled={isDisabled}
                        isRenewalCandidate={isRenewalCandidate}
                      />
                    }
                    onChange={() => toggleDecision(decisionType)}
                    disabled={isDisabled || isExisting || isBlockedByExclusiveDecision}
                    checked={isSelected}
                  />
                </div>
              ),
            )}
          </VBox>
        ),
      )}
    </div>
  );
});

const RecentMatters = observer(
  ({
    projectList,
    starred,
    companies,
  }: {
    projectList: ProjectListState;
    starred: boolean;
    companies: ProjectCompany[];
  }) => {
    const i18n = useI18n();
    const t = i18n.t;
    const allItems = getExistingDecisions(projectList, { starred });
    const items = getFilteredExistingDecisions(projectList, { starred, i18n });
    const dispatch = useDispatch();

    if (isPending(projectList.existingDecisionsResult)) {
      return <LoadingIndicator />;
    }

    if (!isOk(projectList.existingDecisionsResult)) {
      return <Alert type="error" message={t("general-error")} />;
    }

    if (!allItems.length) {
      return (
        <VBox marginBottom={"lg"}>
          {starred ? (
            <EmptyState
              illustration={<img src={emptyStateStarredMatters} alt={"Empty state illustration"} />}
              title={t("dbot.project-list.starred-matters.empty-state.title")}
              description={t("dbot.project-list.starred-matters.empty-state.description")}
            />
          ) : (
            <EmptyState
              illustration={<img src={emptyStateRecentMatters} alt={"Empty state illustration"} />}
              title={t("dbot.project-list.recent-matters.empty-state.title")}
              description={t("dbot.project-list.recent-matters.empty-state.description")}
            />
          )}
        </VBox>
      );
    }

    return (
      <VBox gap="lg">
        <Box wrap>
          <TextInput
            type="search"
            icon="magnifying-glass"
            placeholder={{ key: "search" }}
            value={projectList.existingDecisionsFreetextFilter}
            onChange={(event) => dispatch(setExistingDecisionsFilter(event.target.value))}
            inline
          />
          <Select {...getMatterTypeSelect(projectList)} grow={false} />
          <Select {...getExistingDecisionsCompanySelect(projectList, companies)} grow={false} />
          {!isShowingAll(projectList) && (
            <LinkButton
              className="ml-md"
              icon={"arrow-counter-clockwise"}
              label={t("showAll")}
              onClick={showAll()}
            />
          )}
        </Box>

        {items.length === 0 ? (
          <VBox alignItems={"center"} gap={"md"} marginY={"lg"}>
            <div>{t("dbot.project-list.recent-matters.no-matters-matching-filter")}</div>
          </VBox>
        ) : (
          <DataTable
            variant={"IN_CARD"}
            withRowBorders
            tableData={items}
            getId={(d) => d.decisionId}
            onRowClick={(d) => toggleExistingDecision(i18n, d)}
            rowProps={(d) => ({
              className:
                d === projectList.existingDecisionPreview?.existingDecision
                  ? "bg-primary-light"
                  : undefined,
            })}
            rowActions={(d) =>
              compact<RowAction>([
                d.attachments?.length && [
                  "react-element",
                  <Popover
                    key={"attachments"}
                    button={
                      <Button
                        key={"attachments"}
                        variant={"basic"}
                        size={"sm"}
                        icon={"paperclip"}
                        label={d.attachments?.length}
                      />
                    }
                    content={
                      <VBox alignItems="start">
                        {d.attachments?.map((a) => (
                          <LinkButton
                            key={a.id}
                            icon={"file"}
                            label={a.filename}
                            size="sm"
                            onClick={openAttachment(d, a.id)}
                            weight={"normal"}
                          />
                        ))}
                      </VBox>
                    }
                  />,
                ],
                [
                  "react-element",
                  <Button
                    key={"preview"}
                    className="mr-md"
                    variant={"basic"}
                    size={"sm"}
                    label={
                      <Box alignItems="center" gap={false}>
                        <span>{t("preview")}</span>
                        <Icon name={"caret-right"} />
                      </Box>
                    }
                    onClick={toggleExistingDecisionPreview(d, projectList.existingDecisionPreview)}
                  />,
                ],
              ])
            }
            tableColumns={[
              {
                name: "star",
                compact: true,
                align: "left",
                label: "",
                getValue: (d) => (
                  <StarButton
                    isStarred={d.isStarred}
                    onClick={() => dispatch(toggleDecisionStarred(d))}
                    tooltip={{ key: "dbot.star-matter-tooltip" }}
                  />
                ),
                getSortValue: (d) => d.isStarred,
              },
              {
                name: "check",
                compact: true,
                label: "",
                getValue: (d): ["checkbox", CheckboxProps] => [
                  "checkbox",
                  {
                    checked: projectList.checkedExistingDecisions.some(
                      (x) => d.decisionId === x.decisionId,
                    ),
                    onChange: (event) => toggleExistingDecision(i18n, d, event.target.checked),
                  },
                ],
                getSortValue: (d) =>
                  projectList.checkedExistingDecisions.some((x) => d.decisionId === x.decisionId),
              },
              {
                name: "title",
                align: "left",
                label: { key: "dbot.matters" },
                getClassName: () => "truncate max-w-0",
                getValue: (d) => (
                  <VBox gap={false} className="truncate">
                    <span className="truncate">{getSelectedDecisionLabel(i18n, d)}</span>
                    <small className="truncate subtle">{d.projectName}</small>
                  </VBox>
                ),
                getSortValue: (d) => getSelectedDecisionLabel(i18n, d),
              },
              {
                name: "company",
                align: "left",
                label: { key: "company" },
                getClassName: () => "truncate max-w-0",
                getValue: (d) => d.companyName,
                getSortValue: (d) => d.companyName,
              },
            ]}
          />
        )}
      </VBox>
    );
  },
);

const RecentMatterPreview = observer(
  ({ existingDecisionPreview }: { existingDecisionPreview: ExistingDecisionPreview }) => {
    const i18n = useI18n();
    const dispatch = useDispatch();

    return (
      <VBox bg grow overflow="hidden" paddingTop={"lg"} gap={false}>
        <Box paddingX={"lg"} justifyContent="space-between" alignItems="center" marginTop>
          <h4>{getSelectedDecisionLabel(i18n, existingDecisionPreview.existingDecision)}</h4>
          <CloseButton color="gray" onClick={closeExistingDecisionPreview} />
        </Box>

        {isPending(existingDecisionPreview.previews) && (
          <VBox grow={"golden"} justifyContent="center">
            <LoadingIndicator />
          </VBox>
        )}

        {isOk(existingDecisionPreview.previews) && (
          <>
            <VBox borderBottom>
              <Tabs
                scrollable
                tabs={getExistingDecisionPreviewTabs(
                  existingDecisionPreview,
                  existingDecisionPreview.previews.data,
                )}
              />
            </VBox>
            <VBox gap={false} overflow={"auto"}>
              <DocumentPreview
                content={getExistingDecisionPreviewContent(existingDecisionPreview)}
                padding={false}
                dropShadow={false}
                disableOverflow
              />
              {when(
                getExistionDecisionAttachments(
                  existingDecisionPreview.existingDecision,
                  existingDecisionPreview,
                ),
                (attachments) => (
                  <VBox className="px-4 pb-4 lg:px-12 lg:pb-12 xl:px-24 xl:pb-24">
                    <AttachmentsPanel
                      title={i18n.t("dbot.project-list.matter-attachments", {
                        count: attachments.length,
                        matterTitle: getSelectedDecisionLabel(
                          i18n,
                          existingDecisionPreview.existingDecision,
                        ),
                      })}
                      uploadedAttachments={attachments}
                      onOpen={(id) =>
                        dispatch(openAttachment(existingDecisionPreview.existingDecision, id))
                      }
                    />
                  </VBox>
                ),
              )}
            </VBox>
          </>
        )}
      </VBox>
    );
  },
);

const IncorporateSubsidiaries = observer(
  ({ projectList, companies }: { projectList: ProjectListState; companies: ProjectCompany[] }) => {
    const i18n = useI18n();
    const { t } = i18n;
    const companyName = companies.find((c) => c.orgNo === projectList.orgNo)?.name ?? "";
    const decisions = [{ decisionType: DecisionType.incorporation }];
    return (
      <Grid gap={"lg"} alignItems={"start"}>
        <GridItem xs={12} lg={7}>
          <VBox>
            <Markdown
              value={t("dbot.project-list.incorporate-subsidiaries.content", { companyName })}
              enableLinks
            />
            <div>
              {" "}
              <Button
                variant={"primary"}
                label={t("dbot.project-list.incorporate-subsidiaries.new")}
                onClick={addProjectEvents(projectList, {
                  decisions,
                  projectName: createProjectName({ decisions, i18n }),
                })}
              />
            </div>
          </VBox>
        </GridItem>
        <GridItem xs={12} lg={5}>
          <VBox gap={"lg"} alignItems={"end"}>
            <img
              src={IncorporationIllustration}
              alt=""
              aria-hidden="true"
              className={"max-w-full"}
            />
          </VBox>
        </GridItem>
      </Grid>
    );
  },
);

const ProjectCreationTabs = observer(
  ({
    projectList,
    subscription,
    featureToggles,
    companies,
  }: {
    projectList: ProjectListState;
    subscription: PaidSubscription | undefined;
    featureToggles?: FeatureToggles;
    companies: ProjectCompany[];
  }) => {
    const i18n = useI18n();
    const dispatch = useDispatch();
    return (
      <VBox gap="lg">
        <Tabs tabs={getTabs(projectList)} />
        {projectList.selectedTab === "company-events" ? (
          <WhatWouldYouLikeToDo
            decisionsByCategory={getProjectListDecisionsByCategory(
              projectList,
              subscription,
              featureToggles,
            )}
            toggleDecision={(decision) => dispatch(toggleDecision(i18n, decision))}
          />
        ) : projectList.selectedTab === "incorporate-subsidiaries" ? (
          <IncorporateSubsidiaries projectList={projectList} companies={companies} />
        ) : (
          <RecentMatters
            projectList={projectList}
            starred={projectList.selectedTab === "starred-matters"}
            companies={companies}
          />
        )}
      </VBox>
    );
  },
);

export const DecisionTypeHeader = ({
  label,
  isNew,
  subscriptionTypeModel,
}: {
  label: string;
  isNew?: boolean;
  subscriptionTypeModel: SubscriptionTypeModel;
}) => {
  const { t } = useTranslation();
  return (
    <Box display={"flex"} alignItems={"center"}>
      <strong>{label}</strong>
      {isNew && <Tag label={t("dbot.new")} color={"info"} compact />}
      {requiresBasicSubscription(subscriptionTypeModel) && (
        <Tag
          tooltip={t("dbot.requiresBasicSubscription.tooltip")}
          label={t("dbot.requiresBasicSubscription.label")}
          color={"success"}
        />
      )}
      {requiresProSubscription(subscriptionTypeModel) && (
        <Tag
          label={t("dbot.requiresProSubscription.label")}
          color={"success"}
          tooltip={t("dbot.requiresProSubscription.tooltip")}
        />
      )}
    </Box>
  );
};

export type SubscriptionProps = {
  currentSubscription: PaidSubscription | undefined;
  requiredSubscriptionType: SubscriptionType | undefined;
  isDisabled: boolean;
  isLoading: boolean;
  onNavigateToPayment: () => void;
};

export const RequireSubscriptionButton: FC<{
  subscriptionProps: SubscriptionProps;
}> = observer(({ subscriptionProps }) => {
  const { t } = useTranslation();
  return (
    <>
      <Button
        variant={"primary"}
        label={t(
          requiresBasicSubscription(subscriptionProps)
            ? "dbot.requiresBasicSubscription.continue"
            : "dbot.requiresProSubscription.continue",
        )}
        onClick={subscriptionProps.onNavigateToPayment}
        loading={subscriptionProps.isLoading}
        disabled={subscriptionProps.isDisabled}
      />
      <InfoButton infoText={t("dbot.requiresProSubscription.tooltip")} />
    </>
  );
});

export const ProjectsTable: FC<{ projectList: ProjectListState; companies: ProjectCompany[] }> =
  observer(({ projectList, companies }) => {
    const showOtherPortfolioProjects = hasOtherPortfolioProjects(projectList);
    const { t } = useTranslation();
    const { formatDate } = useLocaleFormat();

    const tableColumns: Array<TableColumn<ProjectListItem>> = useMemo(
      () =>
        compact([
          {
            name: "projectId",
            label: "",
            align: "left",
            width: "30px",
            getValue: ({ isBatchProject }) => <Icon name={isBatchProject ? "folders" : "folder"} />,
          },
          {
            name: "projectName",
            label: t("decisions.name"),
            align: "left",
            width: "50%",
            getValue: (project) => (
              <VBox display="flex">
                <div>{project.projectName}</div>
                {project.isBatchProject && (
                  <div className={"subtle text-sm"}>
                    {project.batchProjectCompanyName
                      ? t("dbot.batchProjectIn", { companyName: project.batchProjectCompanyName })
                      : t("dbot.batchProject")}
                  </div>
                )}
              </VBox>
            ),
            getSortValue: (project) => project.projectName,
          },
          showOtherPortfolioProjects && {
            name: "projectPrimaryCompany",
            label: t("dbot.project-list.primary-company"),
            align: "left",
            width: "50%",
            getValue: (project: ProjectListItem) => (
              <Box whitespace={"nowrap"} textSize={"sm"}>
                <div>{projectPrimaryCompanyName(companies, project.organizationnumber)}</div>
              </Box>
            ),
          },
          {
            name: "projectStatus",
            label: t("common.status"),
            align: "left",
            getValue: ({ projectStatus }) => <ProjectStatusTag status={projectStatus} />,
            getSortValue: (project) => project.projectStatus,
          },
          {
            name: "updatedTime",
            label: t("transactions.last-edit"),
            align: "left",
            getValue: (project) => (
              <small className={"subtle whitespace-nowrap"}>
                {formatDate(project.updatedTime)}
              </small>
            ),
            getSortValue: (project) => project.updatedTime,
          },
          {
            name: "sharing",
            label: t("sharing"),
            align: "left",
            getValue: (project) =>
              project.numberOfPermissions > 0 &&
              project.isMainCompany && (
                <ChangePermissionDialogButton
                  numberOfpermissions={project.numberOfPermissions}
                  onClick={() => openChangePermissionDialog(project)}
                />
              ),
            getSortValue: (project) => project.numberOfPermissions,
          },
        ]),
      [showOtherPortfolioProjects, companies, formatDate, t],
    );

    return (
      <>
        <DataTable
          inline
          onRowClick={(row) => openProject(row, { newWindow: !row.isMainCompany })}
          tableColumns={tableColumns}
          tableData={getProjects(projectList)}
          rowActions={(row) =>
            row.isMainCompany
              ? [
                  [
                    "icon",
                    {
                      icon: "copy",
                      tooltip: { key: "dbot.duplicate" },
                      onClick: () =>
                        openDuplicateProjectDialog(
                          projectList,
                          companies,
                          row,
                          t("dbot.duplicate.projectName", { name: row.projectName }),
                        ),
                    },
                  ],
                  [
                    "icon",
                    {
                      icon: "trash",
                      tooltip: { key: "remove" },
                      color: "error",
                      onClick: () => selectForDelete(row),
                    },
                  ],
                ]
              : [
                  [
                    "react-element",
                    <Box
                      key="opens-in-main-company"
                      fg="subtle"
                      alignItems="center"
                      whitespace="nowrap"
                      textSize="xs"
                    >
                      <span>
                        {t("dbot.project-list.opens-in-main-company", {
                          companyName: projectPrimaryCompanyName(companies, row.organizationnumber),
                        })}
                      </span>
                      <Icon name="arrow-square-out" />
                    </Box>,
                  ],
                ]
          }
          getId={(row) => row.id}
        />
        {Boolean(projectList.projectToDelete) && (
          <ConfirmDeleteProjectDialog
            isBatchProject={projectList.projectToDelete?.isBatchProject}
            onDelete={deleteSubmit(projectList)}
            onCancel={selectForDelete(null)}
          />
        )}
      </>
    );
  });

const NewBatchProjectDialog = observer(
  ({
    projectList,
    companies,
    subscription,
  }: {
    projectList: ProjectListState;
    companies: ProjectCompany[];
    subscription: PaidSubscription | undefined;
  }) => {
    const i18n = useI18n();
    const { t } = i18n;
    const dispatch = useDispatch();

    const isSubmitDisabled =
      projectList.selectedCompanies.length === 0 || projectList.checkedBatchDecisions.length === 0;
    const decisions = getProjectListDecisionsByCategoryForBatch(projectList, subscription);
    const isSubmitting = isPending(projectList.createResult);
    const onSubmit = createBatchProject(projectList, i18n);
    const onCancel = cancelCreateBatchProject();

    return (
      <Dialog
        title={t("decisions.what-to-do")}
        submitLabel={t("continue")}
        isSubmitDisabled={isSubmitDisabled}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        onCancel={onCancel}
      >
        <Box gap={"xl"}>
          <VBox>
            <strong>{t("dbot.matters")}</strong>
            {decisions.map((decisions) =>
              decisions.decisions.map((decisionType) => (
                <div key={decisionType.decisionType}>
                  <Checkbox
                    checked={decisionType.isSelected}
                    label={<DecisionLabel decisionType={decisionType.decisionType} />}
                    onChange={() => dispatch(toggleBatchDecision(decisionType.decisionType))}
                  />
                </div>
              )),
            )}
          </VBox>
          <VBox>
            <strong>{t("dbot.companies")}</strong>
            <Box>
              <LinkButton label={t("select-all")} onClick={selectAllCompanies(companies)} /> /{" "}
              <LinkButton label={t("select-none")} onClick={deselectAllCompanies()} />
            </Box>
            {companies.map(({ name, orgNo }) => (
              <Checkbox
                disabled={isMainCompany(companies, orgNo)}
                label={name}
                key={orgNo}
                value={orgNo}
                checked={isCompanySelected(projectList, orgNo)}
                onChange={() => dispatch(toggleCompanySelected(orgNo))}
              />
            ))}
          </VBox>
        </Box>
      </Dialog>
    );
  },
);

const NewProjectPrompt = observer(({ projectList }: { projectList: ProjectListState }) => {
  const i18n = useI18n();
  const { t } = i18n;
  const dispatch = useDispatch();
  const ref = useRef<HTMLFormElement | null>(null);
  const inputId = useElementId();

  return (
    <TransitionGroup className={"relative"}>
      {!isProjectListEmpty(projectList) && (
        <CSSTransition
          classNames={{
            enter: "opacity-0",
            enterActive: "transition duration-200 opacity-100",
            exitActive: "transition duration-200 opacity-0",
          }}
          nodeRef={ref}
          timeout={200}
        >
          <form
            ref={ref}
            className={boxClassNames({ bg: true, borderTop: true, dropShadow: "top", gap: "md" })}
            onSubmit={preventDefault(() => dispatch(addProjectEvents(projectList)))}
          >
            <VBox container paddingTop paddingBottom={"lg"}>
              <Box justifyContent="space-between" alignItems="center">
                <label htmlFor={inputId}>{t("dbot.new-project.title")}</label>
                <CloseButton color="inherit" onClick={clearProjectListSelection()} />
              </Box>
              <AiPrompt
                fileUploadConfig={agenticUploadConfig}
                onPromptChange={(prompt) => dispatch(setPrompt(prompt))}
                onSourceRemove={(source) => dispatch(removePromptSource(i18n, source))}
                // onUploadFiles={
                //   projectList.isAskAvailable ? (files) => addFileSources(agenticApiClient, i18n, files) : undefined
                // }
                promptInputCtrlRef={(ref) => dispatch(setPromptInputCtrlRef(ref))}
                promptInputId={inputId}
                promptPlaceholder={t("dbot.new-project-name.placeholder")}
                prompt={projectList.prompt}
                sources={getPromptSources(projectList, i18n)}
                disabled={isPending(projectList.createResult)}
              />
              <Box justifyContent="end" alignItems="center" gap={"lg"}>
                <LinkButton label={t("cancel")} onClick={clearProjectListSelection()} />
                <Button
                  type={"submit"}
                  variant={"primary"}
                  arrow={"right"}
                  label={t("dbot.create-project")}
                  disabled={!canAddProject(projectList)}
                  loading={isPending(projectList.createResult)}
                />
              </Box>
            </VBox>
          </form>
        </CSSTransition>
      )}
    </TransitionGroup>
  );
});

export const ProjectListCenterPanel = observer(
  ({
    companies,
    subscription,
    featureToggles,
    projectList,
  }: {
    companies: ProjectCompany[];
    subscription?: PaidSubscription;
    featureToggles?: FeatureToggles;
    projectList: ProjectListState;
  }) => {
    const i18n = useI18n();
    const { t } = i18n;
    const dispatch = useDispatch();
    const existingProjectsHeadingRef = useRef<HTMLDivElement>(null);

    return (
      <Container gap={"lg"} marginY>
        {featureToggles?.automatedDbotDecisions && (
          <Card gap={"lg"} padding={"lg"}>
            <Box
              justifyContent={"space-between"}
              alignItems={"start"}
              wrap={isMobileView()}
              gap={"md"}
            >
              <VBox marginRight={"auto"}>
                <h3>{t("decisions.what-to-do")}</h3>
              </VBox>
              {containsProjects(projectList) && (
                <LinkButton
                  label={t("dbot.existingProjects")}
                  icon={"arrow-down"}
                  onClick={() => {
                    existingProjectsHeadingRef.current?.scrollIntoView(true);
                  }}
                />
              )}
              <DropdownMenu
                button={{
                  type: "link",
                  label: t("dbot.new-blank-document"),
                  icon: "file-plus",
                }}
                items={addProjectOptions(projectList, i18n)}
                alignment="end"
              />
            </Box>

            <ProjectCreationTabs
              projectList={projectList}
              subscription={subscription}
              featureToggles={featureToggles}
              companies={companies}
            />
          </Card>
        )}

        {isPending(projectList.projectsResult) ? (
          <LoadingIndicator label={t("dbot.listingProjects.loading")} />
        ) : isFailure(projectList.projectsResult) ? (
          <Alert type="error" message={t("dbot.loadProjectsError")} />
        ) : containsProjects(projectList) ? (
          <>
            <Box
              ref={existingProjectsHeadingRef}
              alignItems="center"
              justifyContent="space-between"
              marginTop={"lg"}
            >
              <h3>{t("dbot.projects")}</h3>
              <Box gap={"lg"}>
                <Box alignItems={"center"}>
                  <Checkbox
                    label={t("dbot.tab.portfolio-projects")}
                    checked={projectList.showAllPortfolioProjects}
                    disabled={!hasOwnProjects(projectList)}
                    onChange={() => dispatch(toggleShowOtherProjectsSelected())}
                  />
                  <InfoButton
                    infoText={t("dbot.tab.portfolio-projects-info", {
                      companyName: companies.find((f) => f.orgNo === projectList.orgNo)?.name ?? "",
                    })}
                  />
                </Box>
                <DropdownMenu
                  button={{
                    type: "button",
                    variant: "secondary",
                    icon: "plus",
                    tooltip: { key: "add" },
                  }}
                  items={projectList ? addProjectOptions(projectList, i18n) : []}
                  alignment="end"
                />
              </Box>
            </Box>
            <ProjectsTable projectList={projectList} companies={companies} />
          </>
        ) : null}
      </Container>
    );
  },
);

export type ProjectListViewProps = {
  orgNo: string;
  alertStore: AlertMessageStore;
  appBar: AppBarData | null;
  companies: ProjectCompany[];
  requiresSubscriptionUpgrade?: boolean;
  subscription?: PaidSubscription;
  featureToggles?: FeatureToggles;
  projectList?: ProjectListState;
};

export const ProjectListView = observer(
  ({
    orgNo,
    alertStore,
    appBar,
    companies,
    subscription,
    featureToggles,
    projectList,
  }: ProjectListViewProps) => {
    const i18n = useI18n();

    if (!subscription?.allowsDbot && !subscription?.allowsOrdinaryGeneralMeeting) {
      return <OrderSubscriptionUpgrade orgNo={orgNo} />;
    }

    if (!projectList) {
      return <FullScreenLayout alertStore={alertStore} appBar={appBar} centerPanel={"loader"} />;
    }

    if (!featureToggles?.automatedDbotDecisions && isPending(projectList.projectsResult)) {
      return <FullScreenLayout alertStore={alertStore} appBar={appBar} centerPanel={"loader"} />;
    }

    if (!featureToggles?.automatedDbotDecisions && !containsProjects(projectList)) {
      return (
        <FullScreenLayout
          alertStore={alertStore}
          appBar={appBar}
          centerPanel={
            <VBox grow={"golden"} justifyContent="center">
              <EmptyState
                illustration={"sparkle"}
                title={{ key: "dbot.welcome" }}
                actions={
                  <DropdownMenu
                    button={{
                      type: "button",
                      variant: "primary",
                      icon: "plus",
                      label: { key: "dbot.new-blank-document" },
                    }}
                    items={projectList ? addProjectOptions(projectList, i18n) : []}
                    alignment="start"
                  />
                }
              />
            </VBox>
          }
        />
      );
    }

    return (
      <>
        <FullScreenLayout
          alertStore={alertStore}
          appBar={appBar}
          disableAppBarShadow
          centerPanelContainerProps={{
            className: classNames(projectList.existingDecisionPreview && "basis-1/2", "relative"),
          }}
          centerPanel={
            <FullScreenLayout
              centerPanel={
                <ProjectListCenterPanel
                  companies={companies}
                  projectList={projectList}
                  featureToggles={featureToggles}
                  subscription={subscription}
                />
              }
              footerPanel={<NewProjectPrompt projectList={projectList} />}
            />
          }
          rightPanelContainerProps={{
            className: classNames(projectList.existingDecisionPreview && "basis-1/2"),
          }}
          rightPanel={
            projectList.existingDecisionPreview ? (
              <RecentMatterPreview existingDecisionPreview={projectList.existingDecisionPreview} />
            ) : undefined
          }
        />
        {projectList.isNewProjectDialogOpen && (
          <NewBatchProjectDialog
            projectList={projectList}
            companies={companies}
            subscription={subscription}
          />
        )}
      </>
    );
  },
);
